import { analytics } from "@/services/analytics.service";

/**
 * Recording lifecycle analytics for the web client.
 *
 * Event names and property names deliberately match the mobile apps
 * (iOS `AnalyticsEvents.swift` / `AnalyticsProperties.swift`, Android
 * `RecordingController.kt`) so web/iOS/Android land in ONE comparable funnel.
 * Do not rename anything here without changing both mobile clients too.
 *
 * Spec: docs/specs/web-recording-instrumentation.md
 */

/** Events that may fire at most once per recording. */
const ONCE_PER_RECORDING = new Set([
  "recording_started",
  "recording_stopped",
  "recording_stop_accepted",
  "recording_completed",
]);

/**
 * `audio_file_id` -> Set of already-emitted once-per-recording events.
 *
 * The store is a singleton that survives SPA navigation and `startRecording()`
 * has no re-entry guard, so a fresh start can be requested twice for one
 * backend recording. This keeps the funnel honest.
 *
 * Scope matters: pause/resume repeat legitimately within one recording, and
 * `recording_failed` can fire for genuinely distinct phases — deduping those
 * would silently destroy real data.
 *
 * In-memory only, so it does NOT dedupe across a page refresh. That case (the
 * >5s orphan fallthrough) is a known product bug, tracked separately; the
 * duplicate stays visible in the data rather than being papered over.
 */
const emitted = new Map();

/**
 * Bound the map — it is keyed per recording and an SPA session can outlive many
 * of them. Insertion-ordered, so dropping the oldest key is FIFO eviction.
 */
const MAX_TRACKED_RECORDINGS = 50;

/** Permission-class `getUserMedia` rejections. Everything else is a real failure. */
const PERMISSION_ERRORS = new Set(["NotAllowedError", "PermissionDeniedError"]);

export const isPermissionError = (error) =>
  PERMISSION_ERRORS.has(error?.name ?? "");

/**
 * Normalised, non-PII error code. Never pass `error.message` — it can carry
 * arbitrary content and telemetry must stay free of user data.
 */
const GENERIC_ERROR_NAMES = new Set(["Error", ""]);

export const errorCode = (error, fallback) => {
  const name = error?.name;
  // `new Error("boom").name` is "Error" — truthy but meaningless. Locally
  // constructed backend failures are all plain Errors, so without this every
  // real failure would report error_code:"Error" instead of its stage code.
  return name && !GENERIC_ERROR_NAMES.has(name) ? name : fallback;
};

function emit(event, audioFileId, properties = {}) {
  if (ONCE_PER_RECORDING.has(event) && audioFileId != null) {
    const seen = emitted.get(audioFileId) ?? new Set();
    if (seen.has(event)) return false;
    seen.add(event);
    emitted.set(audioFileId, seen);
    if (emitted.size > MAX_TRACKED_RECORDINGS) {
      emitted.delete(emitted.keys().next().value);
    }
  }
  analytics.track(event, {
    ...(audioFileId != null ? { audio_file_id: audioFileId } : {}),
    ...properties,
  });
  return true;
}

export const recordingAnalytics = {
  screenViewed: () => emit("recording_screen_viewed", null),

  permissionRequested: () => emit("recording_permission_requested", null),
  permissionGranted: () => emit("recording_permission_granted", null),
  permissionDenied: (error) =>
    emit("recording_permission_denied", null, {
      error_code: errorCode(error, "permission_denied"),
    }),

  started: (audioFileId, chunkIntervalMs) =>
    emit("recording_started", audioFileId, {
      chunk_interval_ms: chunkIntervalMs,
    }),

  paused: (audioFileId, durationSeconds) =>
    emit("recording_paused", audioFileId, {
      duration_seconds: durationSeconds,
    }),

  resumed: (audioFileId, durationSeconds) =>
    emit("recording_resumed", audioFileId, {
      duration_seconds: durationSeconds,
    }),

  stopped: (audioFileId, { durationSeconds, chunkCount, stopReason }) =>
    emit("recording_stopped", audioFileId, {
      duration_seconds: durationSeconds,
      // NOT comparable to mobile: web chunks are ~500ms MediaRecorder blobs and
      // this counts CAPTURED, not uploaded, chunks. Within-web diagnostics only.
      chunk_count: chunkCount,
      stop_reason: stopReason,
    }),

  /**
   * The backend accepted the stop — the weak stage of the shared three-stage
   * funnel `stopped → stop_accepted → completed` (same vocabulary on iOS and
   * Android). Fires on ANY successful /stop, including a 202 whose
   * composition is still asynchronous (recording.service.js:61-68).
   * finalizeUpload() failures are deliberately swallowed as non-fatal;
   * `finalize_upload_succeeded` + `uploaded_bytes` keep that visible rather
   * than hidden.
   */
  stopAccepted: (audioFileId, { durationSeconds, finalizeOk, uploadedBytes }) =>
    emit("recording_stop_accepted", audioFileId, {
      duration_seconds: durationSeconds,
      finalize_upload_succeeded: finalizeOk,
      uploaded_bytes: uploadedBytes,
    }),

  /**
   * Durable completion as observed by this client: /stop answered with the
   * SYNCHRONOUS composed body (HTTP 200/201, FINALIZE_ASYNC off). A 202 must
   * NEVER produce this event — composition is still running and the browser
   * may be gone before it finishes. If FINALIZE_ASYNC is ever enabled for
   * web, this event dropping toward zero is BY DESIGN, not a regression.
   */
  completed: (audioFileId, { durationSeconds, finalizeOk, uploadedBytes }) =>
    emit("recording_completed", audioFileId, {
      duration_seconds: durationSeconds,
      finalize_upload_succeeded: finalizeOk,
      uploaded_bytes: uploadedBytes,
    }),

  failed: (audioFileId, { error, phase, durationBeforeFailure, code }) =>
    emit("recording_failed", audioFileId, {
      error_code: errorCode(error, code ?? `${phase}_failed`),
      phase,
      duration_before_failure: durationBeforeFailure,
    }),

  /**
   * Emitted when the app's orphan detector fires on a LATER page load — i.e. a
   * previous recording ended without a clean stop. Deliberately NOT emitted on
   * `beforeunload`: that fires before the user answers the confirm dialog, so
   * cancelling would produce a false termination, and it is unreliable on
   * mobile besides.
   */
  abnormalTermination: (audioFileId, durationBeforeFailure) =>
    emit("abnormal_recording_termination", audioFileId, {
      duration_before_failure: durationBeforeFailure,
      reason: "orphan_detected",
    }),

  /**
   * One per AssemblyAI live-preview session (each is billed): `started` on
   * AssemblyAI's Begin, `ended` on its Termination. Same names and properties
   * as Android (`RecordingController`) and iOS:
   * speech_model, live_language (the Pro code, or "auto"), recording_language
   * ("unspecified" if unknown), session_index (0 = the recording's first
   * session), aai_session_id; `ended` adds AssemblyAI's session and audio
   * durations.
   */
  liveSessionStarted: (audioFileId, properties) =>
    emit("live_transcript_session_started", audioFileId, properties),
  liveSessionEnded: (audioFileId, properties) =>
    emit("live_transcript_session_ended", audioFileId, properties),

  /** Test seam only — clears the per-recording dedupe state. */
  _resetForTests: () => emitted.clear(),
};

export default recordingAnalytics;
